import React, { useState, useEffect } from 'react';
import { 
  ChartLine, 
  ArrowUp, 
  ArrowDown, 
  Calendar,
  DropHalfBottom,
  Sparkle,
  Target,
  Star,
  Camera,
  ArrowRight,
  CheckCircle,
  WarningCircle,
  Info
} from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { workerFetch } from '../utils/apiUtils';
import { useNavigate } from 'react-router-dom';

const WORKER_URL = 'https://empty-rain-2654.er-hooman.workers.dev';

const SkinAnalytics = ({ latestAnalysis: latestAnalysisProp }) => {
  const [analysisHistory, setAnalysisHistory] = useState({
    analyses: [],
    totalAnalyses: 0,
    latestAnalysis: null,
    firstAnalysis: null,
    progressMetrics: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Always fetch the full analysis history
    fetchAnalysisHistory();
  }, []);

  const fetchAnalysisHistory = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching analysis history...');
      
      const token = localStorage.getItem('firebaseIdToken');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await workerFetch(`${WORKER_URL}/api/analysis/history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Use response directly
      const actualResponse = response;
      
      if (actualResponse && actualResponse.analyses !== undefined) {
        setAnalysisHistory(actualResponse);
        setError(null);
      } else {
        // Set empty analysis history instead of error
        setAnalysisHistory({
          analyses: [],
          totalAnalyses: 0,
          latestAnalysis: null,
          firstAnalysis: null,
          progressMetrics: {}
        });
        setError(null);
      }
    } catch (err) {
      console.error('❌ Error fetching analysis history:', err);
      setError('Failed to fetch analysis data');
      setAnalysisHistory({
        analyses: [],
        totalAnalyses: 0,
        latestAnalysis: null,
        firstAnalysis: null,
        progressMetrics: {}
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fa-IR');
    } catch (error) {
      console.error('Date formatting error:', error, dateString);
      return dateString;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'عالی';
    if (score >= 60) return 'خوب';
    if (score >= 40) return 'متوسط';
    return 'ضعیف';
  };

  const handleAnalysisClick = (analysisId) => {
    console.log('🔍 SkinAnalytics: Navigating to:', `/analysis/${analysisId}`);
    console.log('🔍 Analysis ID:', analysisId);
    navigate(`/analysis/${analysisId}`);
  };

  const calculateTrend = (values) => {
    if (!values || values.length < 2) return { direction: 'stable', percentage: 0 };
    
    const first = values[values.length - 1];
    const last = values[0];
    const change = last - first;
    const percentage = first === 0 ? (change > 0 ? 100 : 0) : (change / first) * 100;
    
    return {
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'stable',
      percentage: Math.abs(percentage)
    };
  };

  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'up': return <ArrowUp size={16} className="text-green-500" />;
      case 'down': return <ArrowDown size={16} className="text-red-500" />;
      default: return <ChartLine size={16} className="text-gray-500" />;
    }
  };

  const getTrendColor = (direction) => {
    switch (direction) {
      case 'up': return 'text-green-600';
      case 'down': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
              <p className="text-gray-600">در حال بارگذاری...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <WarningCircle size={48} className="text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchAnalysisHistory}
                className="bg-pink-500 hover:bg-pink-600 text-white px-6 py-2 rounded-lg transition-colors"
              >
                تلاش مجدد
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state only when no analyses are available
  if (!analysisHistory.analyses || analysisHistory.analyses.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Info size={48} className="text-blue-500 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">هنوز آنالیزی انجام نداده‌اید</p>
              <button
                onClick={() => navigate('/scanpage')}
                className="bg-pink-500 hover:bg-pink-600 text-white px-6 py-2 rounded-lg transition-colors"
              >
                شروع آنالیز
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { analyses, totalAnalyses, latestAnalysis: latestAnalysisFromState, firstAnalysis, progressMetrics = {} } = analysisHistory;
  
  // Calculate progress metrics based on analysis history
  const calculateProgressMetrics = () => {
    if (!analyses || analyses.length === 0) return {};
    
    const latest = analyses[0]; // Most recent analysis
    if (analyses.length === 1) {
      // Only one analysis - no change
      return {
        hydration: 0,
        clarity: 0,
        texture: 0,
        overall: 0,
        oiliness: 0,
        dryness: 0,
        pores: 0,
        acne: 0
      };
    }
    
    let previous;
    if (analyses.length === 2) {
      // Two analyses - compare with previous
      previous = analyses[1];
    } else {
      // More than two analyses - compare with average of last two
      const lastTwo = analyses.slice(1, 3);
      previous = {
        hydration: Math.round(lastTwo.reduce((sum, a) => sum + (a.hydration || 0), 0) / lastTwo.length),
        clarity: Math.round(lastTwo.reduce((sum, a) => sum + (a.clarity || 0), 0) / lastTwo.length),
        texture: Math.round(lastTwo.reduce((sum, a) => sum + (a.texture || 0), 0) / lastTwo.length),
        overallScore: Math.round(lastTwo.reduce((sum, a) => sum + (a.overallScore || 0), 0) / lastTwo.length),
        oiliness: Math.round(lastTwo.reduce((sum, a) => sum + (a.oiliness || 0), 0) / lastTwo.length),
        dryness: Math.round(lastTwo.reduce((sum, a) => sum + (a.dryness || 0), 0) / lastTwo.length),
        pores: Math.round(lastTwo.reduce((sum, a) => sum + (a.pores || 0), 0) / lastTwo.length),
        acne: Math.round(lastTwo.reduce((sum, a) => sum + (a.acne || 0), 0) / lastTwo.length)
      };
    }
    
    const calculateChange = (current, previous) => {
      if (!current || !previous) return 0;
      return Math.round(((current - previous) / previous) * 100);
    };
    
    return {
      hydration: calculateChange(latest.hydration, previous.hydration),
      clarity: calculateChange(latest.clarity, previous.clarity),
      texture: calculateChange(latest.texture, previous.texture),
      overall: calculateChange(latest.overallScore, previous.overallScore),
      oiliness: calculateChange(latest.oiliness, previous.oiliness),
      dryness: calculateChange(latest.dryness, previous.dryness),
      pores: calculateChange(latest.pores, previous.pores),
      acne: calculateChange(latest.acne, previous.acne)
    };
  };
  
  const calculatedProgressMetrics = calculateProgressMetrics();
  

  // Prepare chart data
  const chartData = analyses && analyses.length > 0 ? analyses.slice().reverse().map((analysis, index) => {
    return {
      date: formatDate(analysis.date),
      hydration: analysis.hydration || 0,
      clarity: analysis.clarity || 0,
      texture: analysis.texture || 0,
      overall: analysis.overallScore || 0
    };
  }) : [];
  

  // Calculate trends
  const hydrationTrend = analyses && analyses.length > 0 ? calculateTrend(analyses.map(a => a.hydration || 0)) : { direction: 'stable', percentage: 0 };
  const clarityTrend = analyses && analyses.length > 0 ? calculateTrend(analyses.map(a => a.clarity || 0)) : { direction: 'stable', percentage: 0 };
  const textureTrend = analyses && analyses.length > 0 ? calculateTrend(analyses.map(a => a.texture || 0)) : { direction: 'stable', percentage: 0 };
  const overallTrend = analyses && analyses.length > 0 ? calculateTrend(analyses.map(a => a.overallScore || 0)) : { direction: 'stable', percentage: 0 };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-pink-100">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-pink-200 rounded-full flex items-center justify-center">
              <ChartLine size={24} className="text-pink-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">تحلیل‌های پوست</h1>
              <p className="text-gray-600">تاریخچه کامل آنالیزهای شما</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-xl">
              <div className="text-2xl font-bold text-blue-600">{totalAnalyses}</div>
              <div className="text-sm text-blue-500">تعداد آنالیز</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-xl">
              <div className="text-2xl font-bold text-green-600">
                {latestAnalysisFromState?.overallScore || 0}
              </div>
              <div className="text-sm text-green-500">امتیاز فعلی</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-xl">
              <div className="text-2xl font-bold text-purple-600">
                {latestAnalysisProp?.clarity || latestAnalysisFromState?.clarity || 0}
              </div>
              <div className="text-sm text-purple-500">شفافیت</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-xl">
              <div className="text-2xl font-bold text-orange-600">
                {calculatedProgressMetrics?.overall > 0 ? '+' : ''}{calculatedProgressMetrics?.overall || 0}%
              </div>
              <div className="text-sm text-orange-500">پیشرفت کلی</div>
            </div>
          </div>
        </div>

        {/* Skin Health Summary */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-pink-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">خلاصه وضعیت پوست</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
              <div className="text-3xl font-bold text-green-600 mb-2">
                {latestAnalysisFromState?.clarity || 0}
              </div>
              <div className="text-sm text-green-600 font-medium">شفافیت</div>
              <div className="text-xs text-green-500 mt-1">
                {calculatedProgressMetrics?.clarity > 0 ? `+${calculatedProgressMetrics.clarity}% بهبود` : 
                 calculatedProgressMetrics?.clarity < 0 ? `${calculatedProgressMetrics.clarity}% کاهش` : 'بدون تغییر'}
              </div>
            </div>
            
            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {latestAnalysisFromState?.texture || 0}
              </div>
              <div className="text-sm text-blue-600 font-medium">بافت</div>
              <div className="text-xs text-blue-500 mt-1">
                {calculatedProgressMetrics?.texture > 0 ? `+${calculatedProgressMetrics.texture}% بهبود` : 
                 calculatedProgressMetrics?.texture < 0 ? `${calculatedProgressMetrics.texture}% کاهش` : 'بدون تغییر'}
              </div>
            </div>
            
            <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
              <div className="text-3xl font-bold text-purple-600 mb-2">
                {latestAnalysisFromState?.acne || 0}
              </div>
              <div className="text-sm text-purple-600 font-medium">آکنه</div>
              <div className="text-xs text-purple-500 mt-1">
                {calculatedProgressMetrics?.acne > 0 ? `+${calculatedProgressMetrics.acne}% بهبود` : 
                 calculatedProgressMetrics?.acne < 0 ? `${calculatedProgressMetrics.acne}% کاهش` : 'بدون تغییر'}
              </div>
            </div>
          </div>
        </div>

        {/* Progress Chart */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-pink-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">روند پیشرفت</h3>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span>آبرسانی</span>
              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
              <span>شفافیت</span>
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>بافت</span>
              <div className="w-3 h-3 bg-pink-500 rounded-full"></div>
              <span>امتیاز کلی</span>
            </div>
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="hydration" stroke="#3B82F6" strokeWidth={2} />
                <Line type="monotone" dataKey="clarity" stroke="#8B5CF6" strokeWidth={2} />
                <Line type="monotone" dataKey="texture" stroke="#10B981" strokeWidth={2} />
                <Line type="monotone" dataKey="overall" stroke="#EC4899" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Key Skin Metrics Progress */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-pink-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">پیشرفت معیارهای مهم پوست</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Primary Metrics */}
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <DropHalfBottom size={18} className="text-blue-600" />
                  <span className="text-gray-700 text-sm">آبرسانی</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${calculatedProgressMetrics?.hydration >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {calculatedProgressMetrics?.hydration > 0 ? '+' : ''}{calculatedProgressMetrics?.hydration || 0}%
                  </span>
                  <span className="text-xs text-gray-500">
                    ({latestAnalysisProp?.hydration || latestAnalysisFromState?.hydration || 0})
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Sparkle size={18} className="text-purple-600" />
                  <span className="text-gray-700 text-sm">شفافیت</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${calculatedProgressMetrics?.clarity >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {calculatedProgressMetrics?.clarity > 0 ? '+' : ''}{calculatedProgressMetrics?.clarity || 0}%
                  </span>
                  <span className="text-xs text-gray-500">
                    ({latestAnalysisProp?.clarity || latestAnalysisFromState?.clarity || 0})
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Target size={18} className="text-green-600" />
                  <span className="text-gray-700 text-sm">بافت</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${calculatedProgressMetrics?.texture >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {calculatedProgressMetrics?.texture > 0 ? '+' : ''}{calculatedProgressMetrics?.texture || 0}%
                  </span>
                  <span className="text-xs text-gray-500">
                    ({latestAnalysisProp?.texture || latestAnalysisFromState?.texture || 0})
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-pink-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Star size={18} className="text-pink-600" />
                  <span className="text-gray-700 text-sm">امتیاز کلی</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${calculatedProgressMetrics?.overall >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {calculatedProgressMetrics?.overall > 0 ? '+' : ''}{calculatedProgressMetrics?.overall || 0}%
                  </span>
                  <span className="text-xs text-gray-500">
                    ({latestAnalysisProp?.overallScore || latestAnalysisFromState?.overallScore || 0})
                  </span>
                </div>
              </div>
            </div>

            {/* Secondary Metrics */}
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">چربی پوست</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${calculatedProgressMetrics?.oiliness >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {calculatedProgressMetrics?.oiliness > 0 ? '+' : ''}{calculatedProgressMetrics?.oiliness || 0}%
                  </span>
                  <span className="text-xs text-gray-500">
                    ({latestAnalysisProp?.oiliness || latestAnalysisFromState?.oiliness || 0})
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">خشکی پوست</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${calculatedProgressMetrics?.dryness >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {calculatedProgressMetrics?.dryness > 0 ? '+' : ''}{calculatedProgressMetrics?.dryness || 0}%
                  </span>
                  <span className="text-xs text-gray-500">
                    ({latestAnalysisProp?.dryness || latestAnalysisFromState?.dryness || 0})
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 bg-indigo-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">منافذ</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${calculatedProgressMetrics?.pores >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {calculatedProgressMetrics?.pores > 0 ? '+' : ''}{calculatedProgressMetrics?.pores || 0}%
                  </span>
                  <span className="text-xs text-gray-500">
                    ({latestAnalysisProp?.pores || latestAnalysisFromState?.pores || 0})
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">آکنه</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${calculatedProgressMetrics?.acne >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {calculatedProgressMetrics?.acne > 0 ? '+' : ''}{calculatedProgressMetrics?.acne || 0}%
                  </span>
                  <span className="text-xs text-gray-500">
                    ({latestAnalysisProp?.acne || latestAnalysisFromState?.acne || 0})
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Analysis History */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-pink-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">تاریخچه آنالیزها</h3>
          <div className="space-y-4">
            {analyses && analyses.length > 0 ? analyses.map((analysis, index) => (
              <div
                key={analysis.id}
                onClick={() => handleAnalysisClick(analysis.id)}
                className="p-4 bg-gray-50 hover:bg-gray-100 rounded-xl cursor-pointer transition-colors border border-gray-200 hover:border-pink-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-pink-200 rounded-full flex items-center justify-center">
                      <Camera size={20} className="text-pink-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar size={16} className="text-gray-500" />
                        <span className="text-sm text-gray-600">{formatDate(analysis.date)}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-blue-600">آبرسانی: {analysis.hydration || 0}</span>
                        <span className="text-purple-600">شفافیت: {analysis.clarity || 0}</span>
                        <span className="text-green-600">بافت: {analysis.texture || 0}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className={`text-lg font-bold ${getScoreColor(analysis.overallScore || 0)}`}>
                        {analysis.overallScore || 0}
                      </div>
                      <div className="text-xs text-gray-500">{getScoreLabel(analysis.overallScore || 0)}</div>
                    </div>
                    <ArrowRight size={20} className="text-gray-400" />
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center py-8 text-gray-500">
                <p>هیچ آنالیزی یافت نشد</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkinAnalytics;